import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CHARS = '01ABCDEF<>[]{}|\\/#░▒▓';

function ScrambleText({ target, delay = 0, duration = 700 }) {
  const [display, setDisplay] = useState('');
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  useEffect(() => {
    if (!started) return;
    let frame = 0;
    const totalFrames = 22;
    const intervalMs = duration / totalFrames;

    const id = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      const resolved = Math.floor(progress * target.length);
      const result = target
        .split('')
        .map((ch, i) => {
          if (i < resolved) return ch;
          if (ch === ' ' || ch === '.' || ch === '-') return ch;
          return CHARS[Math.floor(Math.random() * CHARS.length)];
        })
        .join('');
      setDisplay(result);
      if (frame >= totalFrames) {
        setDisplay(target);
        clearInterval(id);
      }
    }, intervalMs);

    return () => clearInterval(id);
  }, [started, target, duration]);

  if (!started) return null;
  return <span>{display || target}</span>;
}

export default function LoadingScreen({ onComplete }) {
  const [phase, setPhase] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [mounted, setMounted] = useState(true);

  const complete = useCallback(() => {
    sessionStorage.setItem('portfolio_loaded', '1');
    setExiting(true);
    setTimeout(() => {
      setMounted(false);
      onComplete && onComplete();
    }, 500);
  }, [onComplete]);

  useEffect(() => {
    if (sessionStorage.getItem('portfolio_loaded')) {
      setMounted(false);
      onComplete && onComplete();
      return;
    }

    const t1 = setTimeout(() => setPhase(1), 100);   // log lines appear fast
    const t2 = setTimeout(() => setPhase(2), 900);    // progress + KINGSLEY.O reveal
    const t3 = setTimeout(() => setPhase(3), 1600);   // system online
    const t4 = setTimeout(complete, 2400);             // exit — tight total

    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, [complete]);

  if (!mounted) return null;

  return (
    <AnimatePresence>
      {!exiting && (
        <motion.div
          key="loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: 'easeInOut' }}
          className="fixed inset-0 z-[9999] bg-[#0a0a0a] flex flex-col items-center justify-center overflow-hidden"
          data-testid="loading-screen"
        >
          {/* Grid fades in at phase 2 */}
          <motion.div
            className="absolute inset-0 grid-pattern"
            animate={{ opacity: phase >= 2 ? 0.18 : 0 }}
            transition={{ duration: 1 }}
          />

          {/* Horizontal scan line */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <motion.div
              className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent"
              animate={{ top: ['0%', '100%'] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'linear' }}
            />
          </div>

          {/* Vertical accent line left */}
          <motion.div
            className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-cyan-500/20 to-transparent"
            initial={{ scaleY: 0 }}
            animate={{ scaleY: phase >= 1 ? 1 : 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />

          {/* Main content */}
          <div className="relative z-10 font-orbitron max-w-lg w-full px-10">
            {/* Header line */}
            {phase >= 1 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="text-xs text-cyan-500/50 tracking-[0.5em] uppercase mb-8"
              >
                <ScrambleText target="PORTFOLIO RUNTIME v2.4.1" delay={0} duration={500} />
              </motion.div>
            )}

            {/* Log lines */}
            {phase >= 1 && (
              <div className="space-y-2 mb-8">
                {[
                  { text: '[INIT]  Booting portfolio runtime...', delay: 0, color: 'text-white/25' },
                  { text: '[OK]    Core modules compiled', delay: 200, color: 'text-white/25' },
                  { text: '[OK]    3D scene context ready', delay: 380, color: 'text-white/25' },
                  { text: '[LOAD]  Establishing connection...', delay: 560, color: 'text-cyan-400/50' },
                ].map((line, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.12 }}
                    className={`text-xs tracking-wider font-rajdhani ${line.color}`}
                  >
                    <ScrambleText target={line.text} delay={line.delay} duration={450} />
                  </motion.div>
                ))}
              </div>
            )}

            {/* Progress bar */}
            {phase >= 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mb-8"
              >
                <div className="h-px w-full bg-white/8 overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-cyan-500 to-blue-500"
                    initial={{ width: '0%' }}
                    animate={{ width: phase >= 3 ? '100%' : phase >= 2 ? '70%' : '35%' }}
                    transition={{ duration: 0.8, ease: 'easeInOut' }}
                  />
                </div>
              </motion.div>
            )}

            {/* Big name reveal */}
            {phase >= 2 && (
              <motion.div
                initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
                className="border-t border-white/8 pt-6"
              >
                <div className="text-5xl font-black tracking-[0.08em] text-white leading-none">
                  <ScrambleText target="KINGSLEY.O" delay={0} duration={700} />
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="text-xs text-cyan-400/70 tracking-[0.45em]">
                    <ScrambleText target="SYSTEM ONLINE" delay={300} duration={500} />
                  </span>
                </div>
              </motion.div>
            )}
          </div>

          {/* Corner: top-left */}
          <div className="absolute top-7 left-10 text-xs text-white/15 font-orbitron tracking-wider">
            {phase >= 1 && <ScrambleText target="KO/PORTFOLIO" delay={400} duration={400} />}
          </div>

          {/* Corner: bottom-right — removed year text */}

          {/* Corner brackets */}
          <div className="absolute top-6 left-6 w-5 h-5 border-t border-l border-cyan-500/30" />
          <div className="absolute top-6 right-6 w-5 h-5 border-t border-r border-cyan-500/30" />
          <div className="absolute bottom-6 left-6 w-5 h-5 border-b border-l border-cyan-500/30" />
          <div className="absolute bottom-6 right-6 w-5 h-5 border-b border-r border-cyan-500/30" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
